/**
 * statusMap — the SINGLE source of truth for the client-facing status ladder's
 * VISUAL identity (icon + short label + soft-badge pill triad + tone), keyed by the
 * derived rung `key` (deriveClientRung / claim_ladder.key).
 *
 * This is presentation only. It does NOT recompute status — it maps an
 * already-derived rung key to its Lucide icon + Tailwind classes so the pill
 * (StatusPill) and vertical timeline (StatusTimeline) render one professional
 * icon + colour per rung instead of an emoji. The plain-language BLURB and the
 * full client-facing phrasing (e.g. "Shipped, awaiting your confirmation") live in
 * the page (LADDER_BLURB + the derived rung label), NOT here — the map's `label` is
 * the SHORT pill label only.
 *
 * Tailwind can't see dynamically-interpolated class names, so every class used by
 * the pill AND the timeline (ring/text-by-tone) is written out statically here in
 * lookup objects.
 */
import {
  Inbox,
  Hammer,
  FlaskConical,
  PackageCheck,
  CircleCheck,
  ShieldCheck,
  RotateCcw,
  Ban,
  Clock,
  GitMerge,
  CircleMinus,
  type LucideIcon,
} from 'lucide-react';

export type StatusTone =
  | 'slate'
  | 'blue'
  | 'amber'
  | 'violet'
  | 'emerald'
  | 'red'
  | 'muted';

export interface StatusConfig {
  icon: LucideIcon;
  /** SHORT pill label — NOT the full client-facing phrasing (that stays in the page). */
  label: string;
  /** Tailwind triad for the soft-badge pill (border + bg + text). */
  pill: string;
  tone: StatusTone;
  /** true for the filled (solid-fill) terminal pill — you_confirmed. */
  filled?: boolean;
}

export const STATUS_MAP: Record<string, StatusConfig> = {
  received: {
    icon: Inbox,
    label: 'Received',
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'slate',
  },
  in_progress: {
    icon: Hammer,
    label: 'In progress',
    pill: 'border-blue-200 bg-blue-50 text-blue-700',
    tone: 'blue',
  },
  building_testing: {
    icon: FlaskConical,
    label: 'In testing',
    pill: 'border-amber-200 bg-amber-50 text-amber-800',
    tone: 'amber',
  },
  shipped_awaiting: {
    icon: PackageCheck,
    label: 'Shipped',
    pill: 'border-violet-200 bg-violet-50 text-violet-700',
    tone: 'violet',
  },
  verified: {
    icon: CircleCheck,
    label: 'Verified',
    pill: 'border-emerald-200 bg-emerald-50 text-emerald-700',
    tone: 'emerald',
  },
  you_confirmed: {
    icon: ShieldCheck,
    label: 'Confirmed by you',
    // F1 (WCAG-AA): emerald-600 on white text is ~3.77:1 (fails AA); emerald-700
    // clears 4.5:1 for the filled pill.
    pill: 'border-emerald-700 bg-emerald-700 text-white',
    tone: 'emerald',
    filled: true,
  },
  // v0.17.15: the reporter said "still broken" on a shipped fix.
  still_broken_reported: {
    icon: RotateCcw,
    label: 'Still broken',
    pill: 'border-red-200 bg-red-50 text-red-700',
    tone: 'red',
  },
  regressed: {
    icon: RotateCcw,
    label: 'Reopened',
    pill: 'border-red-200 bg-red-50 text-red-700',
    tone: 'red',
  },
  wont_do: {
    icon: Ban,
    label: "Won't do",
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'muted',
  },
  deferred: {
    icon: Clock,
    label: 'Deferred',
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'muted',
  },
  superseded: {
    icon: GitMerge,
    label: 'Merged',
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'muted',
  },
  withdrawn: {
    icon: CircleMinus,
    label: 'Withdrawn',
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'muted',
  },
  // v0.17.16: a report the operator dismissed from triage.
  closed: {
    icon: CircleMinus,
    label: 'Closed',
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'muted',
  },
};

/**
 * Concrete tone -> ring/text classes for the timeline's CURRENT node (Tailwind can't
 * resolve `ring-${tone}-500`). Static so the JIT scanner keeps them.
 */
export const CURRENT_NODE_RING: Record<StatusTone, string> = {
  slate: 'ring-slate-500',
  blue: 'ring-blue-500',
  amber: 'ring-amber-500',
  violet: 'ring-violet-500',
  emerald: 'ring-emerald-500',
  red: 'ring-red-500',
  muted: 'ring-slate-400',
};

export const CURRENT_NODE_TEXT: Record<StatusTone, string> = {
  slate: 'text-slate-600',
  blue: 'text-blue-600',
  amber: 'text-amber-600',
  violet: 'text-violet-600',
  emerald: 'text-emerald-600',
  red: 'text-red-600',
  muted: 'text-slate-500',
};

/**
 * Resolve the visual config for a rung key, with a neutral slate fallback for any
 * unknown key (so nothing renders un-styled). The fallback borrows the passed-in
 * label (the derived rung's own label) as the short pill label.
 */
export function getStatusConfig(key: string, fallbackLabel = ''): StatusConfig {
  const config = STATUS_MAP[key];
  if (config) return config;
  return {
    icon: Inbox,
    label: fallbackLabel,
    pill: 'border-slate-200 bg-slate-50 text-slate-600',
    tone: 'slate',
  };
}
